import { FC } from 'react'
import { Box, SelectChangeEvent, Typography } from '@mui/material'
import SelectV2 from 'ui/components/form-components-v2/select-v2'
import SelectWithPopoverMenuV2 from 'ui/components/form-components-v2/select-with-popover-menu-v2'
import { JiraDataMapping, JiraMappingFieldCatalogItem, JiraOptionValue } from 'models'
import SectionLoading from '../components/SectionLoading'
import { StepBody } from '../../jiraWizard.styles'
import { SEVERITY_MENU_PROPS } from '../data-configuration/dataConfiguration.configs'
import {
  MappingEmptyState,
  MappingListWrapper,
  fieldSelectSx
} from '../data-configuration/dataConfiguration.styles'
import {
  CLOSED_STATUS_SLOT,
  isDoneCategory,
  resolveOptionValues,
  toOption
} from '../data-configuration/dataConfiguration.utils'
import { JiraOptionSource } from '../data-configuration/useJiraOptions'

const SEVERITY_HINT = 'Priorities left unmapped are excluded from severity calculations.'

/* An empty list is a dead end here: the mapping cannot be completed and Save
 * stays disabled. Name the cause rather than showing empty selects. */
const emptySourceHint = (statuses: JiraOptionSource, priorities: JiraOptionSource) => {
  const failed = [
    statuses.warning ? `statuses (${statuses.warning})` : '',
    priorities.warning ? `priorities (${priorities.warning})` : ''
  ].filter(Boolean)

  if (failed.length) return `Jira options failed to load: ${failed.join(', ')}`

  const empty = [!statuses.values.length ? 'statuses' : '', !priorities.values.length ? 'priorities' : ''].filter(
    Boolean
  )

  if (!empty.length) return ''

  return `Jira returned no ${empty.join(' or ')}, so there is nothing to map. Check that they are defined in Jira.`
}

const PLACEHOLDER = 'Select'

const LOCKED_TITLE = 'No Data Filters Found'
const LOCKED_DESCRIPTION = 'You must have at least one filter in order to map data fields'

/* The label the legacy page used once the done-category statuses were applied. */
const DEFAULTS_LABEL = 'Default'

interface Props {
  mapping: JiraDataMapping
  onMappingChange: (mapping: JiraDataMapping) => void
  mappingFields: JiraMappingFieldCatalogItem[]
  statuses: JiraOptionSource
  priorities: JiraOptionSource
  loadingOptions?: boolean
  disabled?: boolean
  /* The mapping options only exist once the filters step is applied. */
  locked?: boolean
}

/* Data Mapping step: which statuses count as closed, and which Jira priorities
 * fill each of Onyxia's severity levels — Onyxia's fields on the left, the Jira
 * values they map to on the right. */
const DataMappingStep: FC<Props> = ({
  mapping,
  onMappingChange,
  mappingFields,
  statuses,
  priorities,
  loadingOptions,
  disabled,
  locked
}) => {
  const closedStatusField = mappingFields.find((field) => field.onyxia_field === CLOSED_STATUS_SLOT)
  const severityFields = mappingFields.filter((field) => field.onyxia_field !== CLOSED_STATUS_SLOT)
  const rows = [closedStatusField, ...severityFields].filter((field): field is JiraMappingFieldCatalogItem => !!field)

  const slotValues = (slot: string) => mapping[slot] ?? []
  const setSlot = (slot: string, values: JiraOptionValue[]) => onMappingChange({ ...mapping, [slot]: values })

  const sourceFor = (slot: string) => (slot === CLOSED_STATUS_SLOT ? statuses : priorities)

  const handleSlotChange = (slot: string, names: string[]) =>
    setSlot(slot, resolveOptionValues(names, sourceFor(slot).values, slotValues(slot)))

  /* Defaults are computed here, not served: the done-category statuses, flagged so
   * a resumed wizard can tell them from hand-picked ones. */
  const doneStatuses = statuses.values.filter(isDoneCategory)

  const selectedDefaults = doneStatuses.filter((status) =>
    slotValues(CLOSED_STATUS_SLOT).some((value) => value.name === status.name)
  ).length

  /* Checked only when the slot holds exactly the defaults; toggling off clears it. */
  const defaultsChecked =
    !!doneStatuses.length &&
    selectedDefaults === doneStatuses.length &&
    slotValues(CLOSED_STATUS_SLOT).length === doneStatuses.length

  const toggleStatusDefaults = () =>
    setSlot(CLOSED_STATUS_SLOT, defaultsChecked ? [] : doneStatuses.map((status) => ({ ...status, is_default: true })))

  /* A priority belongs to one severity level only: the slot holding it keeps it
   * selectable, the others show it disabled. */
  const takenByOtherSeverity = (slot: string) =>
    new Set(
      severityFields
        .filter((field) => field.onyxia_field !== slot)
        .flatMap((field) => slotValues(field.onyxia_field).map((value) => value.name))
    )

  /* One priority per severity level; the step payload stays a list of 0 or 1. */
  const severityOptions = (slot: string) => {
    const taken = takenByOtherSeverity(slot)

    return priorities.values.map((priority) => ({
      label: priority.display_name || priority.name,
      value: priority.name,
      disabled: taken.has(priority.name)
    }))
  }

  const selectSeverity = (slot: string, name: string) =>
    setSlot(slot, name ? resolveOptionValues([name], priorities.values, slotValues(slot)) : [])

  const renderSlotValue = (slot: string) => {
    const values = slotValues(slot)
    if (!values.length) return PLACEHOLDER
    if (values.every((value) => value.is_default)) return DEFAULTS_LABEL

    return values.map((value) => value.display_name || value.name).join(', ')
  }

  const showDefaultsAction = !!closedStatusField?.has_default && !!doneStatuses.length

  const optionsHint = emptySourceHint(statuses, priorities)

  if (locked) {
    return (
      <StepBody>
        <MappingEmptyState>
          <Typography className="empty-state-title">{LOCKED_TITLE}</Typography>
          <Typography className="empty-state-description">{LOCKED_DESCRIPTION}</Typography>
        </MappingEmptyState>
      </StepBody>
    )
  }

  return (
    <StepBody>
      <MappingListWrapper>
        <Box className="section">
          <Typography className="header">Onyxia</Typography>
          <Box className="list">
            {loadingOptions ? (
              <SectionLoading length={rows.length || 5} />
            ) : (
              rows.map((field) => (
                <Box className="list-item-onyxia" key={field.onyxia_field}>
                  <Typography className="list-item-text">{field.display_name}</Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <Box className="section">
          <Typography className="header">Jira</Typography>
          <Box className="list">
            {loadingOptions ? (
              <SectionLoading length={rows.length || 5} />
            ) : (
              rows.map((field) => {
                const slot = field.onyxia_field
                const isClosedStatus = slot === CLOSED_STATUS_SLOT

                /* A severity level takes exactly one priority. */
                if (!isClosedStatus) {
                  return (
                    <SelectV2
                      key={slot}
                      id={`mapping-${slot}`}
                      label=""
                      searchable
                      displayEmpty
                      optionControl="radio"
                      MenuProps={SEVERITY_MENU_PROPS}
                      options={severityOptions(slot)}
                      value={slotValues(slot)[0]?.name ?? ''}
                      disabled={disabled}
                      sx={fieldSelectSx(!!slotValues(slot).length)}
                      renderValue={() => renderSlotValue(slot)}
                      listAction={{ label: 'Reset filter', onClick: () => setSlot(slot, []) }}
                      onChange={(e: SelectChangeEvent<unknown>) => selectSeverity(slot, e.target.value as string)}
                    />
                  )
                }

                return (
                  <SelectWithPopoverMenuV2
                    key={slot}
                    label=""
                    labelId={`mapping-${slot}`}
                    searchable
                    displayEmpty
                    placeholder={PLACEHOLDER}
                    selectAllCheckboxLabel="All statuses"
                    options={statuses.values.map(toOption)}
                    value={slotValues(slot).map(toOption)}
                    disabled={disabled}
                    sx={fieldSelectSx(!!slotValues(slot).length)}
                    listAction={
                      showDefaultsAction
                        ? {
                            label: `Default statuses (${doneStatuses.length})`,
                            checked: defaultsChecked,
                            indeterminate: selectedDefaults > 0 && !defaultsChecked,
                            onToggle: toggleStatusDefaults
                          }
                        : undefined
                    }
                    renderSelected={() => renderSlotValue(slot)}
                    onChange={(selected) =>
                      handleSlotChange(
                        slot,
                        selected.map((option) => option.value as string)
                      )
                    }
                  />
                )
              })
            )}
          </Box>
        </Box>
      </MappingListWrapper>

      {!loadingOptions && !!optionsHint ? (
        <Typography className="section-error">{optionsHint}</Typography>
      ) : (
        <Typography className="section-hint">{SEVERITY_HINT}</Typography>
      )}
    </StepBody>
  )
}

export default DataMappingStep
